import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MediaQueryService} from '@mydocma/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {LayoutEditService} from '../layout-edit-service';
import {TranslateService} from '@ngx-translate/core';
import {UntypedFormBuilder} from '@angular/forms';
import {LayoutApiService} from '@docma-mm-core/layout/layout-api.service';
import {BehaviorSubject, combineLatest, from, map, Observable} from 'rxjs';
import {FieldService} from '@docma-mm-core/field/field.service';
import {MmViewService} from '../../../../mm-view.service';
import {LayoutEditDetailsComponent} from '../layout-edit-details/layout-edit-details.component';
import {Layout} from '@docma-mm-core/layout/layout';
import {LayoutEditFieldsComponent} from '../layout-edit-fields/layout-edit-fields.component';
import {LayoutFieldExt} from '@docma-mm-core/layout/layout-field';
import {Field} from '@docma-mm-core/field/field';
import {MatLegacyTabChangeEvent as MatTabChangeEvent, MatLegacyTabGroup as MatTabGroup} from '@angular/material/legacy-tabs';
import {FilterApiService} from '@docma-mm-core/filter/filter-api.service';
import {LayoutFilter} from '@docma-mm-core/layout/layout-filter';
import {LayoutEditFiltersComponent} from '../layout-edit-filters/layout-edit-filters.component';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {User} from '@docma-mm-core/user/user';
import {Project} from '@docma-mm-core/project/project';
import {ViewPoint} from '@docma-mm-core/status/viewpoint.enum';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {LayoutDuplicateMode} from '../layout-duplicate-dialog/layout-duplicate-dialog.component';
import {AppContainerService} from '@mydocma/app-container';
import { Logger } from '@mydocma/common/src/lib/logger';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-layout-edit-dialog',
  templateUrl: './layout-edit-dialog.component.html',
  styleUrls: ['./layout-edit-dialog.component.scss'],
})
export class LayoutEditDialogComponent implements OnInit, AfterViewInit {


  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: {layoutId: number, scope: EntityScopeEnum, project: Project, user: User, service: LayoutEditService, isNew: boolean, duplicateMode: LayoutDuplicateMode, duplicateType: EntityScopeEnum, nameSuffix: string},
              private dialogRef: MatDialogRef<LayoutEditDialogComponent>,
              private translateService: TranslateService,
              public readonly mediaQueryService: MediaQueryService,
              private readonly formBuilder: UntypedFormBuilder,
              public appContainerService: AppContainerService,
              private readonly layoutApiService: LayoutApiService,
              private readonly filterApiService: FilterApiService,
              private readonly fieldService: FieldService,
              private readonly viewService: MmViewService,
              public readonly projectLoaderService: MmProjectLoaderService,
              private readonly notificationService: NotificationService,
              private readonly dialogService: PromptDialogService,
              private readonly logger: Logger) {

    /* LOAD ALL: LAYOUT + ALL FILTERS + ALL FIELDS */
    this.loadingSubject.next(true);

    this.titleSubject.next((this.dialogData.isNew ? this.translateService.instant('MM.layout.titleNewLayout') : this.translateService.instant('MM.layout.titleEditLayout')));

    combineLatest([this.loadLayout(dialogData),
                          this.fieldService.getConfigurationProjectFields(this.viewService.currentProjectSubject.value.Id),
                          this.filterApiService.getAllFilters(),
                          this.fieldService.getProjectFields(dialogData.project.Id, false, null, null, 10000, 0, false),
                          this.loadHasReferences(dialogData?.layoutId)

    ]).pipe(untilDestroyed(this))
      .subscribe(([layout, fields, filters, permittedFields, hasReferences]) => {

        if (!this.dialogData.isNew && this.dialogData.duplicateMode == null) {
          this.titleSubject.next(this.translateService.instant('MM.layout.titleEditLayout') + ' - ' + layout.Name);
        }

        const fieldsInternal: {[id: number]: LayoutFieldExt} = [];
        const filtersInternal: {[id: number]: LayoutFilter} = [];

        const permittedFieldsDict: {[FieldId: number]: boolean} = [];
        const alwaysPermittedFieldsDict: string[] = [
          'structure_level1_name1', 'structure_level2_name1', 'structure_level3_name1', 'structure_level4_name1', 'structure_level5_name1', 'structure_level6_name1', 'structure_level7_name1', 'structure_level8_name1', 'structure_level9_name1', 'structure_level10_name1', 'structure_level1_name2', 'structure_level2_name2', 'structure_level3_name2', 'structure_level4_name2', 'structure_level5_name2', 'structure_level6_name2', 'structure_level7_name2', 'structure_level8_name2', 'structure_level9_name2', 'structure_level10_name2', 'structure_name1', 'structure_name2', 'structure_shortpath', 'structure_longpath', 'structure_displaypath'
        ];

        permittedFields.Data.forEach(p => {
          permittedFieldsDict[p.Id] = (p.Permission != null ? p.Permission != -1 : false);
        });

        layout.LayoutFilters.forEach(x => x.IsAssigned = true);
        this.layoutLoaded = layout;

        const tempLayout = JSON.parse(JSON.stringify(layout));

        fields.forEach (f => {
          const lf = layout.LayoutFields.find(lf => lf.Field.Id === +f.Id);
          const lfEx = new LayoutFieldExt(
            {
              FieldMinimal: f,
              IsAssigned: false,
              HasPermission: (permittedFieldsDict.hasOwnProperty(f.Prop) ? permittedFieldsDict[f.Prop] : (alwaysPermittedFieldsDict.includes(f.Prop.toLowerCase()) ? true : false))
            });

          if (lf) {
            lfEx.LayoutField = lf;
            fieldsInternal[f.Id] = lfEx;
          }
          else {
            const lfId = (0 - (+f.Id));
            lfEx.LayoutField = { Id: lfId, Field: new Field({Id: + f.Id, DefaultWidth: f.DefaultWidth, DataType: f.DataType }) };
            fieldsInternal[lfId] = lfEx;
          }
        });

        tempLayout.LayoutFields.forEach(lf => {
          if (fieldsInternal.hasOwnProperty(lf.Field.Id)) {
            const perm = (permittedFieldsDict.hasOwnProperty(lf.Field.DefaultName) ? permittedFieldsDict[lf.Field.DefaultName] : false);

            lf.Field.DefaultWidth = fieldsInternal[lf.Field.Id].FieldMinimal.DefaultWidth;
            fieldsInternal[lf.Field.Id].LayoutField = lf;
            fieldsInternal[lf.Field.Id].IsAssigned = true;
            fieldsInternal[lf.Field.Id].HasPermission = perm;
          }
        });

        filters.forEach (f => {
          f.Scope = (f.User == null && f.Project == null ? EntityScopeEnum.Global : (f.Project != null ? EntityScopeEnum.Project : EntityScopeEnum.User));
          filtersInternal[f.Id] = new LayoutFilter({ Filter: f, IsAssigned: false });
        });

        tempLayout.LayoutFilters.forEach(lf => {
          if (filtersInternal.hasOwnProperty(lf.Filter.Id)) {
            filtersInternal[lf.Filter.Id].Id = lf.Id;
            filtersInternal[lf.Filter.Id].IsAssigned = true;
            filtersInternal[lf.Filter.Id].LayoutId = lf.LayoutId;
          }
        });



        this.editArgs = {Layout: tempLayout, Fields: Object.values(fieldsInternal), Filters: Object.values(filtersInternal), IsNew: dialogData.isNew, project: this.dialogData.project, user: this.dialogData.user, hasReferences};
        this.filterTabRequired = this.editArgs.Layout.IsOverviewLayout;
        this.overviewLayoutSubject.next(this.editArgs.Layout.IsOverviewLayout);
        this.filterCountSubject.next(this.editArgs.Layout.LayoutFilters.length);

        this.detailComponent.initialize(this.editArgs);
        this.detailComponent.detailsService.valueChanged
          .pipe(untilDestroyed(this)).subscribe(x => {
          this.hasChanges();
        });
        this.detailComponent.detailsService.hasErrors$
          .pipe(untilDestroyed(this))
          .subscribe(hasErrors => {
            this.canSaveSubject.next(!hasErrors);
          });



        this.fieldsComponent.initialize(this.editArgs);
        this.fieldsComponent.valueChanged
          .pipe(untilDestroyed(this)).subscribe(x => this.hasChanges());

        this.filterComponent.initialize(this.editArgs);
        this.filterComponent.valueChanged
          .pipe(untilDestroyed(this)).subscribe(x => this.hasChanges());

        this.loadingSubject.next(false);
      });
  }

  @ViewChild('editDetails') detailComponent?: LayoutEditDetailsComponent;
  @ViewChild('editFields') fieldsComponent?: LayoutEditFieldsComponent;
  @ViewChild('editFilters') filterComponent?: LayoutEditFiltersComponent;
  @ViewChild('tabGroupLayout') tabGroupLayout?: MatTabGroup;

  layoutLoaded: Layout;
  filterTabRequired = false;
  cantSaveTooltip: string;
  loadingSubject = new BehaviorSubject<Boolean>(true);
  loading$ = this.loadingSubject.asObservable();
  savingSubject = new BehaviorSubject<Boolean>(false);
  saving$ = this.savingSubject.asObservable();

  titleSubject = new BehaviorSubject<string>('');
  title$ = this.titleSubject.asObservable();

  editArgs?: {Layout: Layout, Fields: LayoutFieldExt[], Filters: LayoutFilter[], IsNew: boolean, project: Project, user: User, hasReferences: boolean};
  private overviewLayoutSubject = new BehaviorSubject<boolean>(false);
  private filterCountSubject = new BehaviorSubject<number>(0);

  hasChangesSubject = new BehaviorSubject<boolean>(false);
  hasChanges$ = this.hasChangesSubject.asObservable();

  canSaveSubject = new BehaviorSubject<boolean>(true);
  canSave$ = combineLatest([
    this.canSaveSubject,
    this.filterCountSubject,
    this.overviewLayoutSubject
  ]).pipe(
    map(([canSave, filterCount, isOverviewLayout]) => {
      return canSave && (!isOverviewLayout || (isOverviewLayout && filterCount > 0));
    })
  );

  cantSaveTooltip$ = combineLatest([
    this.canSaveSubject,
    this.filterCountSubject,
    this.overviewLayoutSubject
  ]).pipe(
    map(([canSave, filterCount, isOverviewLayout]) => {
      if (isOverviewLayout && (!canSave || filterCount === 0)) {
        return this.translateService.instant('MM.layout.filterRequiredInOverviewTooltip');
      }
      return '';
    })
  );

  ngOnInit(): void {

  }

  ngAfterViewInit() {
    this.detailComponent.detailsService.onOverviewLayoutChanged
    .pipe(untilDestroyed(this))
      .subscribe(isOverviewLayout => {

        this.filterTabRequired = isOverviewLayout;
        this.overviewLayoutSubject.next(isOverviewLayout);
      });

    this.filterComponent.filtersService.selectedFiltersCount$
      .pipe(untilDestroyed(this))
      .subscribe(count => {
        this.filterCountSubject.next(count);
      });

    this.detailComponent.detailsService.preInitialize(this.dialogData.scope);
    this.detailComponent.detailsService.onTypeChanged
      .pipe(untilDestroyed(this))
      .subscribe(scope => {
        this.filterComponent.onScopeChanged(scope);
      });

    const tabClickHandle = this.tabGroupLayout._handleClick;

    this.tabGroupLayout._handleClick = (tab, header, index) => {
      if (this.tabGroupLayout.selectedIndex === 0) {
        if (this.detailComponent.detailsService.validate()) {
          // this.detailComponent.detailsService.commit();
          tabClickHandle.apply(this.tabGroupLayout, [tab, header, index]);
        }
      }
      else if (this.tabGroupLayout.selectedIndex === 1) {
        if (this.fieldsComponent.fieldsService.validate()) {
          tabClickHandle.apply(this.tabGroupLayout, [tab, header, index]);
        }
      }
      else if (this.tabGroupLayout.selectedIndex === 2) {
        if (this.filterComponent.filtersService.validate()) {
          tabClickHandle.apply(this.tabGroupLayout, [tab, header, index]);
        }
      }
    };
  }

  loadHasReferences(layoutId?: number): Observable<boolean>  {
    if (layoutId) {
      return this.layoutApiService.getHasReferences(layoutId);
    }
    else {
      return from(Promise.resolve(false));
    }
  }
  loadLayout(dialogData: {layoutId: number, scope: EntityScopeEnum, project: Project, user: User, service: LayoutEditService, isNew: boolean}): Observable<Layout> {
    if (dialogData.layoutId) {
      return this.layoutApiService.getFullLayout(dialogData.layoutId);
    }
    else {
      const newLayout = new Layout();
      newLayout.LayoutFields = [];
      newLayout.LayoutFilters = [];
      newLayout.Scope = dialogData.scope;

      switch (dialogData.scope) {
        case EntityScopeEnum.Project:
          newLayout.Project = dialogData.project;
          break;

        case EntityScopeEnum.User:
          newLayout.User = dialogData.user;
          break;
      }

      switch (dialogData.user.Company.ViewPoint) {
        case ViewPoint.Contractor:
          newLayout.IsVisibleForCO = true;
          break;

        case ViewPoint.Customer:
          newLayout.IsVisibleForCT = true;
          break;

        case ViewPoint.Subcontractor:
          newLayout.IsVisibleForSC = true;
          break;
      }

      newLayout.IsFavoriteUser = true;

      return from(Promise.resolve(newLayout));
    }
  }

  onSelectTab(args: MatTabChangeEvent) {
    if (args.index === 0) {
      // this.detailComponent?.onShow();
    }
    else if (args.index === 1) {
      this.fieldsComponent?.onShow();
    }
    else if (args.index === 2) {
      this.filterComponent?.onShow();
    }
  }


  async onCancel() {
    if (await this.hasChanges()) {
      const title = await this.translateService.get('MM.dialog.discardChanges.title').toPromise();
      const content = await this.translateService.get('MM.dialog.discardChanges.content').toPromise();

      if (!(await this.dialogService.showDiscardChangesConfirmationDialog({title, content}))?.value) {
        return;
      }
    }

    this.dialogRef.close();
  }

  async getChanges(): Promise<{ Patches: any[], Layout: Layout, ForceReload: boolean }> {
    if (this.editArgs.IsNew) {
      return {Patches: null, Layout: this.editArgs.Layout, ForceReload: false};
    }
    else {
      let forceReload = false;
      const patches: any[] = [];

      if (this.layoutLoaded.IsOverviewLayout != this.editArgs.Layout.IsOverviewLayout) {
        patches.push({ path: '/IsOverviewLayout', op: 'replace', value: this.editArgs.Layout.IsOverviewLayout });
      }

      if (this.layoutLoaded.Name != this.editArgs.Layout.Name) {
        patches.push({ path: '/Name', op: 'replace', value: this.editArgs.Layout.Name});
      }

      if (this.layoutLoaded.Description != this.editArgs.Layout.Description) {
        patches.push({ path: '/Description', op: 'replace', value: this.editArgs.Layout.Description});
      }

      if (this.layoutLoaded.User?.Id != this.editArgs.Layout.User?.Id) {
        patches.push({ path: '/User', op: 'replace', value: (this.editArgs.Layout.User ? `{ "Id": ${this.editArgs.Layout.User.Id}}` : null)});
      }

      if (this.layoutLoaded.Project?.Id != this.editArgs.Layout.Project?.Id) {
        patches.push({ path: '/Project', op: 'replace', value: (this.editArgs.Layout.Project ? `{ "Id": ${this.editArgs.Layout.Project.Id}}` : null)});
      }

      if (this.layoutLoaded.IsFavoriteAll != this.editArgs.Layout.IsFavoriteAll) {
        patches.push({ path: '/IsFavoriteAll', op: 'replace', value: this.editArgs.Layout.IsFavoriteAll});
      }

      if (this.layoutLoaded.IsFavoriteUser != this.editArgs.Layout.IsFavoriteUser) {
        patches.push({ path: '/IsFavoriteUser', op: 'replace', value: this.editArgs.Layout.IsFavoriteUser});
      }

      if (this.layoutLoaded.IsVisibleForCO != this.editArgs.Layout.IsVisibleForCO) {
        patches.push({ path: '/IsVisibleForCO', op: 'replace', value: this.editArgs.Layout.IsVisibleForCO});
      }

      if (this.layoutLoaded.IsVisibleForCT != this.editArgs.Layout.IsVisibleForCT) {
        patches.push({ path: '/IsVisibleForCT', op: 'replace', value: this.editArgs.Layout.IsVisibleForCT});
      }

      if (this.layoutLoaded.IsVisibleForSC != this.editArgs.Layout.IsVisibleForSC) {
        patches.push({path: '/IsVisibleForSC', op: 'replace', value: this.editArgs.Layout.IsVisibleForSC});
      }


      this.editArgs.Fields.filter(x => this.layoutLoaded.LayoutFields.findIndex(y => y.Field.Id === x.LayoutField.Field.Id) === -1 && x.IsAssigned)
        .forEach(f => {
          const _f = this.layoutLoaded.LayoutFields.find(x => x.Field.Id === f.LayoutField.Field.Id);
          if (!_f && f.IsAssigned) {
            patches.push({path: '/LayoutFields', op: 'add',
              value: `{
                        "Field": { "Id": ${f.LayoutField.Field.Id} },
                        "Width": ${f.FieldMinimal.DefaultWidth},
                        "SortId": ${f.LayoutField.SortId},
                        "Grouping": 0,
                        "Sorting": 0,
                        "IsDescending": false
                      }`});

            forceReload = true;
          }
        });

      // removed fields
      const removedFields: number[] = [];
      this.layoutLoaded.LayoutFields.filter(x => this.editArgs.Fields.findIndex(y => y.LayoutField.Field.Id === x.Field.Id) !== -1)
        .forEach(f => {
          const _f = this.editArgs.Fields.find(x => x.LayoutField.Field.Id === f.Field.Id);
          if (_f?.LayoutField && !_f.IsAssigned) {
            removedFields.push(_f.LayoutField.Field.Id);
            patches.push({path: `/LayoutFields/${_f.LayoutField.Id}`, op: 'remove', value: null});
          }
        });

      // changed fields
      this.layoutLoaded.LayoutFields.forEach(f => {
        const _f = this.editArgs.Fields.find(x => x.LayoutField.Field.Id === f.Field.Id);
        if (_f?.LayoutField && f.SortId !== _f.LayoutField.SortId && removedFields.findIndex(r => r === f.Field.Id) === -1) {
          patches.push({path: `/LayoutFields/${_f.LayoutField.Id}/SortId`, op: 'replace', value: _f.LayoutField.SortId});
        }
      });

      // added filters
      this.editArgs.Filters.filter(x => this.layoutLoaded.LayoutFilters.findIndex(y => y.Filter.Id === x.Filter.Id) === -1 && x.IsAssigned)
        .forEach(f => {
          const _f = this.layoutLoaded.LayoutFilters.find(x => x.Filter.Id === f.Filter.Id);
          if (!_f && f.IsAssigned) {
            patches.push({path: '/LayoutFilters', op: 'add',
              value: `{
                        "Filter": { "Id": ${f.Filter.Id} },
                      }`});

            forceReload = true;
          }
        });

      // removed filters
      this.layoutLoaded.LayoutFilters.filter(x => this.editArgs.Filters.findIndex(y => y.Filter.Id === x.Filter.Id) !== -1 && x.IsAssigned)
        .forEach(f => {
          const _f = this.editArgs.Filters.find(x => x.Filter.Id === f.Filter.Id);
          if (!_f.IsAssigned) {
            patches.push({path: `/LayoutFilters/${_f.Id}`, op: 'remove', value: null});

            forceReload = true;
          }
        });

      return {Patches: patches, Layout: null, ForceReload: forceReload};
    }
  }

  async hasChanges() {
    if (this.editArgs == null) {
      return false;
    }

    if (this.editArgs.IsNew) {
      this.hasChangesSubject.next(true);
      return true;
    }
    else {
      let hasChanges = false;

      if (this.layoutLoaded.Name != this.editArgs.Layout.Name
          || this.layoutLoaded.Description != this.editArgs.Layout.Description
          || this.layoutLoaded.User?.Id != this.editArgs.Layout.User?.Id
          || this.layoutLoaded.Project?.Id != this.editArgs.Layout.Project?.Id
          || this.layoutLoaded.IsFavoriteAll != this.editArgs.Layout.IsFavoriteAll
          || this.layoutLoaded.IsFavoriteUser != this.editArgs.Layout.IsFavoriteUser
          || this.layoutLoaded.IsVisibleForCO != this.editArgs.Layout.IsVisibleForCO
          || this.layoutLoaded.IsVisibleForCT != this.editArgs.Layout.IsVisibleForCT
          || this.layoutLoaded.IsVisibleForSC != this.editArgs.Layout.IsVisibleForSC
          || this.layoutLoaded.IsOverviewLayout != this.editArgs.Layout.IsOverviewLayout
      ) {
        hasChanges = true;
      }

      // added fields
      this.editArgs.Fields.filter(x => this.layoutLoaded.LayoutFields.findIndex(y => y.Field.Id === x.LayoutField.Field.Id) === -1 && x.IsAssigned)
        .forEach(f => {
          const _f = this.layoutLoaded.LayoutFields.find(x => x.Field.Id === f.LayoutField.Field.Id);
          if (!_f && f.IsAssigned) {
            hasChanges = true;
          }
        });

      // removed fields
      this.layoutLoaded.LayoutFields.filter(x => this.editArgs.Fields.findIndex(y => y.LayoutField.Field.Id === x.Field.Id) !== -1)
        .forEach(f => {
          const _f = this.editArgs.Fields.find(x => x.LayoutField.Field.Id === f.Field.Id);
          if (!_f.IsAssigned) {
            hasChanges = true;
          }
        });

      // changed fields
      this.layoutLoaded.LayoutFields.forEach(f => {
        const _f = this.editArgs.Fields.find(x => x.LayoutField.Field.Id === f.Field.Id);
        if (_f?.LayoutField?.SortId && f.SortId !== _f.LayoutField.SortId) {
          hasChanges = true;
        }
      });

      // added filters
      this.editArgs.Filters.filter(x => this.layoutLoaded.LayoutFilters.findIndex(y => y.Filter.Id === x.Filter.Id) === -1 && x.IsAssigned)
        .forEach(f => {
          const _f = this.layoutLoaded.LayoutFilters.find(x => x.Filter.Id === f.Filter.Id);
          if (!_f && f.IsAssigned) {
            hasChanges = true;
          }
        });

      // removed filters
      this.layoutLoaded.LayoutFilters.filter(x => this.editArgs.Filters.findIndex(y => y.Filter.Id === x.Filter.Id) !== -1 && x.IsAssigned)
        .forEach(f => {
          const _f = this.editArgs.Filters.find(x => x.Filter.Id === f.Filter.Id);
          if (!_f.IsAssigned) {
            hasChanges = true;
          }
        });

      this.hasChangesSubject.next(hasChanges);
      return hasChanges;
    }
  }

  async onSaveChanges() {
    if (!this.detailComponent.validate()) {
      return;
    }

    if (await this.hasChanges()) {
      this.savingSubject.next(true);

      const changes: { Patches: any[], Layout: Layout, ForceReload: boolean } = await this.getChanges();
      const originalIsOverviewLayout = this.layoutLoaded?.IsOverviewLayout;

      if (this.editArgs.IsNew) {
        const newLayout = changes.Layout;
        try {
          const savedLayout: Layout = await this.layoutApiService.createLayout(newLayout).toPromise();
          this.dialogRef.close({ Layout: savedLayout, IsNew: this.dialogData.isNew, ForceReload: changes.ForceReload, originalIsOverviewLayout });
        } catch (err) {
          this.logger.error(err, '', 'LayoutEditDialogComponent');
          this.notificationService.error(this.translateService.instant('MM.layout.errorSavingLayout'));
        }
        finally {
          this.savingSubject.next(false);
        }
      }
      else {
        try {
          const updatedLayout: Layout = await this.layoutApiService.patchLayout(this.editArgs.Layout.Id, changes.Patches).toPromise();
          this.dialogRef.close({ Layout: updatedLayout, IsNew: this.dialogData.isNew, ForceReload: changes.ForceReload, originalIsOverviewLayout });
        } catch (err) {
          this.logger.error(err, '', 'LayoutEditDialogComponent');
          this.notificationService.error(this.translateService.instant('MM.layout.errorUpdatingLayout'));
        }
        finally {
          this.savingSubject.next(false);
        }
      }

    }
  }
}
